import { useState } from 'react';
import { Mail, X, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PresetBadge } from '../shared';
import { usePendingInvitations, useResendInvitation, useCancelInvitation } from '@/hooks/queries';
import { cn } from '@/lib/utils';

function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function expiresIn(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs <= 0) return 'Expired';
  const diffHours = Math.floor(diffMs / 3600000);
  if (diffHours < 24) return `${diffHours}h left`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d left`;
}

interface PendingInvitesProps {
  campaignId: string | undefined;
}

export function PendingInvites({ campaignId }: PendingInvitesProps) {
  const invitationsRead = usePendingInvitations(undefined);
  const { mutate: resend } = useResendInvitation();
  const { mutate: cancel, isPending: cancelPending } = useCancelInvitation();
  const [resendingId, setResendingId] = useState<string | null>(null);

  if (invitationsRead.status === 'failed' || invitationsRead.status === 'offline') {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {invitationsRead.status === 'offline'
          ? "You're offline, so pending invites can't load."
          : "Couldn't load pending invites. Try again shortly."}
      </p>
    );
  }
  const invitations = invitationsRead.status === 'ready' ? invitationsRead.data : [];
  if (invitations.length === 0) return null;

  const handleResend = (invitationId: string) => {
    if (!campaignId) return;
    setResendingId(invitationId);
    resend(
      { campaignId, invitationId },
      { onSettled: () => setResendingId(null) }
    );
  };

  const handleCancel = (invitationId: string) => {
    if (!campaignId) return;
    cancel({ campaignId, invitationId });
  };

  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">Pending Invites</span>
        <Badge variant="secondary" className="text-xs">{invitations.length}</Badge>
      </div>

      <div className="space-y-2">
        {invitations.map((inv) => (
          <div
            key={inv.id}
            className="flex items-center justify-between gap-4 rounded-md border bg-background px-3 py-2 text-sm"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="truncate font-medium">{inv.email}</span>
              <PresetBadge isOwner={false} presetKey={inv.presetKey ?? null} className="shrink-0" />
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Sent {timeAgo(inv.createdAt)}
              </span>
              <span className="text-xs text-muted-foreground hidden md:inline">
                {expiresIn(inv.expiresAt)}
              </span>

              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={() => handleResend(inv.id)}
                disabled={resendingId === inv.id}
              >
                <RefreshCw className={cn('h-3.5 w-3.5', resendingId === inv.id && 'animate-spin')} />
                <span className="ml-1 hidden sm:inline">Resend</span>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-destructive hover:text-destructive"
                onClick={() => handleCancel(inv.id)}
                disabled={cancelPending}
              >
                <X className="h-3.5 w-3.5" />
                <span className="ml-1 hidden sm:inline">Cancel</span>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
